import { Modal, Button, Form, Result, Space, message, theme } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import axios from 'axios';
import { useCreatePago } from '../api/usePagos';
import { useSyncClienteUbicacionFromPago } from '../../clientes/api/useClientes';
import { clientesKeys } from '../../clientes/api/keys';
import { useT } from '../../../locales';
import { PagoForm } from './PagoForm';
import { ReceiptVersionsButton } from './ReceiptVersions';
import {
  ReceiptActionButtons,
  ReceiptPdfViewer,
  useReceiptActions,
  type ReceiptTarget,
} from './ReceiptActions';
import type { ClienteRef, PagoClienteResponse } from '@aid/shared';
import type { CreatePagoResponse } from '../api/pagos-service';

interface CreatedPagoConfirmation {
  id: number;
  receiptTarget: ReceiptTarget | null;
  receiptAvailable: boolean;
}

function isPagoClienteResponse(resultado: CreatePagoResponse): resultado is PagoClienteResponse {
  return 'id' in resultado;
}

function normalizePagoId(resultado: CreatePagoResponse): number {
  return isPagoClienteResponse(resultado) ? resultado.id : resultado.idPago;
}

function hasReceiptApplications(pago: PagoClienteResponse): boolean {
  return (pago.aplicacion?.aplicacionesCreadas ?? pago.aplicacionesCount ?? 0) > 0;
}

function receiptTargetFrom(resultado: CreatePagoResponse): ReceiptTarget | null {
  // ADELANTADO responses carry idPago (no applications materialised yet);
  // the backend can still generate the receipt from persisted package data.
  return isPagoClienteResponse(resultado) ? resultado : { id: resultado.idPago };
}

function receiptAvailableFrom(resultado: CreatePagoResponse): boolean {
  // ADELANTADO offers the receipt immediately; NORMAL keeps the guard — a
  // receipt without applied invoices is still inconsistent for NORMAL.
  return isPagoClienteResponse(resultado) ? hasReceiptApplications(resultado) : true;
}

const PREPAID_ERROR_CODE = {
  STALE: 'PREPAID_CURRENT_INVOICE_STALE',
  OVERLAP: 'PREPAID_INVOICE_OVERLAP',
  FIFO_FORBIDDEN: 'PREPAID_FIFO_PAYMENT_FORBIDDEN',
  SETTLEMENT_FORBIDDEN: 'PREPAID_SETTLEMENT_INVOICE_FORBIDDEN',
  MODALITY_TARGET_CONFLICT: 'PAYMENT_MODALITY_TARGET_CONFLICT',
} as const;

/** Stable backend codes mapped to their specific operator-facing message. */
const PREPAID_ERROR_MESSAGE_KEY: Partial<Record<string, string>> = {
  [PREPAID_ERROR_CODE.STALE]: 'pages.pagos.prepagoPreview.staleError',
  [PREPAID_ERROR_CODE.OVERLAP]: 'pages.pagos.prepagoPreview.overlapError',
  [PREPAID_ERROR_CODE.FIFO_FORBIDDEN]: 'pages.pagos.prepagoPreview.fifoForbiddenError',
  [PREPAID_ERROR_CODE.SETTLEMENT_FORBIDDEN]: 'pages.pagos.prepagoPreview.settlementForbiddenError',
  [PREPAID_ERROR_CODE.MODALITY_TARGET_CONFLICT]:
    'pages.pagos.prepagoPreview.modalityTargetConflictError',
};

function getPrepaidConflictCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return null;
  const code = (data as Record<string, unknown>).error;
  return typeof code === 'string' ? code : null;
}

interface PagoCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Cliente preseleccionado al abrir (p. ej. desde la worklist del cobrador). */
  preselectClienteId?: number;
  /** Compact client data from the cobrador worklist; avoids an admin lookup. */
  preselectedCliente?: ClienteRef;
}

export function PagoCreateModal({
  open,
  onClose,
  onSuccess,
  preselectClienteId,
  preselectedCliente,
}: PagoCreateModalProps) {
  const t = useT();
  const qc = useQueryClient();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const createPago = useCreatePago();
  const syncClienteUbicacion = useSyncClienteUbicacionFromPago();
  // PagoForm blocks this footer while location or receipt OCR is pending.
  const [formSubmitBlocked, setFormSubmitBlocked] = useState(false);
  const [shouldUpdateClientLocation, setShouldUpdateClientLocation] = useState(false);
  const [createdPago, setCreatedPago] = useState<CreatedPagoConfirmation | null>(null);
  const receiptActions = useReceiptActions(createdPago?.receiptTarget ?? null);

  const handleClose = () => {
    receiptActions.closeViewer();
    // The Form instance belongs to this persistent modal component; reset it
    // explicitly because destroyOnHidden only unmounts the form children.
    form.resetFields();
    setCreatedPago(null);
    setShouldUpdateClientLocation(false);
    onClose();
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    try {
      const resultado = await createPago.mutateAsync(
        values as Parameters<typeof createPago.mutateAsync>[0],
      );
      const idPago = normalizePagoId(resultado);
      if (shouldUpdateClientLocation) {
        try {
          await syncClienteUbicacion.mutateAsync(idPago);
          message.success(t('pages.pagos.ubicacionClienteActualizada'));
        } catch {
          message.error(t('pages.pagos.ubicacionClienteError'));
        }
      }
      // Refresh the client 360 financial summary, invoices, and payments.
      qc.invalidateQueries({ queryKey: clientesKeys.detail(resultado.idCliente) });
      qc.invalidateQueries({ queryKey: clientesKeys.cobros(resultado.idCliente) });
      setShouldUpdateClientLocation(false);
      setCreatedPago({
        id: idPago,
        receiptTarget: receiptTargetFrom(resultado),
        receiptAvailable: receiptAvailableFrom(resultado),
      });
      message.success(t('pages.pagos.createSuccess'));
      onSuccess?.();
    } catch (error: unknown) {
      const conflictCode = getPrepaidConflictCode(error);
      const conflictMessageKey = conflictCode ? PREPAID_ERROR_MESSAGE_KEY[conflictCode] : undefined;
      if (conflictMessageKey) {
        message.error(t(conflictMessageKey));
      } else {
        message.error(t('pages.pagos.createError'));
      }
    }
  };

  const footer = createdPago ? (
    <Space wrap size={token.margin}>
      <Button key="close" onClick={handleClose}>
        {t('common.close')}
      </Button>
      <ReceiptVersionsButton idPago={createdPago.id} />
      {createdPago.receiptAvailable && createdPago.receiptTarget ? (
        <ReceiptActionButtons
          actions={receiptActions}
          downloadLabel={t('pages.pagos.downloadReceiptAfterCreate')}
          printLabel={t('pages.pagos.printReceiptAfterCreate')}
          downloadType="primary"
          printType="default"
        />
      ) : null}
    </Space>
  ) : (
    [
      <Button key="cancel" onClick={handleClose}>
        {t('common.cancel')}
      </Button>,
      <Button
        key="submit"
        type="primary"
        loading={createPago.isPending}
        disabled={formSubmitBlocked}
        onClick={() => form.submit()}
      >
        {t('pages.pagos.create')}
      </Button>,
    ]
  );

  return (
    <Modal
      title={t('pages.pagos.create')}
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      centered
      width={720}
      wrapClassName="pago-create-modal"
      classNames={{ body: 'pago-create-modal__body' }}
      styles={{
        // Shell del modal con tokens: el contenedor no aporta padding y cada
        // sección lo declara con tokens semánticos (v6 non-wireframe trae
        // header/body/footer sin padding y footer sin línea divisoria).
        container: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100dvh - 32px)',
        },
        header: {
          flex: '0 0 auto',
          padding: `${token.paddingMD}px ${token.paddingLG}px 0`,
          marginBottom: 0,
        },
        body: {
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          padding: `${token.paddingMD}px ${token.paddingLG}px`,
        },
        footer: {
          flex: '0 0 auto',
          marginTop: token.marginSM,
          padding: `${token.paddingMD}px ${token.paddingLG}px`,
          paddingBottom: `max(${token.paddingMD}px, env(safe-area-inset-bottom, 0px))`,
          borderTop: `${token.lineWidth} ${token.lineType} ${token.colorSplit}`,
        },
      }}
      footer={footer}
    >
      {createdPago ? (
        <>
          <div role="status" aria-live="polite">
            <Result
              status="success"
              title={t('pages.pagos.createSuccess')}
              subTitle={
                createdPago.receiptAvailable
                  ? t('pages.pagos.createSuccessReceiptDescription', { id: createdPago.id })
                  : t('pages.pagos.createSuccessNoReceiptDescription', { id: createdPago.id })
              }
            />
          </div>
          {receiptActions.viewerUrl ? (
            <ReceiptPdfViewer
              url={receiptActions.viewerUrl}
              title={t('pages.pagos.receiptViewerTitle')}
              fallbackMessage={t('pages.pagos.receiptPrintFallback')}
              closeLabel={t('common.close')}
              onClose={receiptActions.closeViewer}
            />
          ) : null}
        </>
      ) : (
        <PagoForm
          mode="create"
          form={form}
          preselectedClienteId={preselectClienteId}
          preselectedCliente={preselectedCliente}
          // The modal owns the form submit action in its footer.
          hideSubmit
          onSubmit={handleSubmit}
          loading={createPago.isPending || syncClienteUbicacion.isPending}
          onLocationBlockedChange={setFormSubmitBlocked}
          onClientLocationUpdateChange={setShouldUpdateClientLocation}
        />
      )}
    </Modal>
  );
}
